import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Check, Calendar, Clock, Video, Shield } from "lucide-react";

const prepChecklist = [
  "Review your quiz results (if you took the quiz)",
  "Think about your 90-day revenue goal",
  "Have your content analytics ready (followers, views, etc.)",
  "Be prepared to discuss your niche and audience",
];

export default function BookCall() {
  return (
    <div className="min-h-screen" style={{ background: "#FAFAFA" }}>
      <Helmet>
        <title>Book Your Strategy Call | AI StoryCraft Studio</title>
        <meta name="description" content="Book your free 30-minute strategy call. We'll map out your AI monetisation system together." />
      </Helmet>

      {/* Success Banner */}
      <div className="w-full py-3 text-center" style={{ backgroundColor: "#DCFCE7" }}>
        <p className="text-sm font-semibold" style={{ color: "#166534" }}>✅ Application approved — book your free strategy call below</p>
      </div>

      <div className="mx-auto px-4 md:px-6 py-12 md:py-16" style={{ maxWidth: 820 }}>
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ color: "#1F2937" }}>
            Book Your Free Strategy Call
          </h1>
          <p className="text-base leading-relaxed max-w-xl mx-auto" style={{ color: "#6B7280" }}>
            In 30 minutes, we'll map out exactly how to build your AI monetisation system — and whether the Done-With-You programme is right for you.
          </p>
        </div>

        {/* Call Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <div className="bg-white rounded-xl p-5 text-center" style={{ border: "1px solid #E5E7EB" }}>
            <Clock className="w-5 h-5 mx-auto mb-2" style={{ color: "#7C3AED" }} />
            <p className="text-sm font-semibold" style={{ color: "#1F2937" }}>30 Minutes</p>
            <p className="text-xs" style={{ color: "#9CA3AF" }}>Focused strategy session</p>
          </div>
          <div className="bg-white rounded-xl p-5 text-center" style={{ border: "1px solid #E5E7EB" }}>
            <Video className="w-5 h-5 mx-auto mb-2" style={{ color: "#7C3AED" }} />
            <p className="text-sm font-semibold" style={{ color: "#1F2937" }}>Via Zoom</p>
            <p className="text-xs" style={{ color: "#9CA3AF" }}>Link sent after booking</p>
          </div>
          <div className="bg-white rounded-xl p-5 text-center" style={{ border: "1px solid #E5E7EB" }}>
            <Shield className="w-5 h-5 mx-auto mb-2" style={{ color: "#7C3AED" }} />
            <p className="text-sm font-semibold" style={{ color: "#1F2937" }}>100% Free</p>
            <p className="text-xs" style={{ color: "#9CA3AF" }}>No obligation whatsoever</p>
          </div>
        </div>

        {/* Calendar Embed Placeholder */}
        <div className="bg-white rounded-2xl p-6 md:p-10 mb-10" style={{ border: "1px solid #E5E7EB" }}>
          <div className="w-full rounded-xl flex items-center justify-center" style={{ minHeight: 500, backgroundColor: "#F9FAFB", border: "2px dashed #E5E7EB" }}>
            <div className="text-center p-8">
              <Calendar className="w-12 h-12 mx-auto mb-4" style={{ color: "#7C3AED" }} />
              <h3 className="text-lg font-bold mb-2" style={{ color: "#1F2937" }}>Calendar Loading...</h3>
              <p className="text-sm max-w-sm mx-auto mb-4" style={{ color: "#6B7280" }}>
                The booking calendar will appear here. If it doesn't load, please refresh the page or contact us at support@ai-storycraft-studio.com
              </p>
              <p className="text-xs" style={{ color: "#9CA3AF" }}>
                Availability: Mon–Fri 7 PM – 2 AM GMT · Sat 3 PM – 2 AM · Sun 7 AM – 11 PM
              </p>
            </div>
          </div>
          {/* 
            To embed Cal.com or Calendly, replace the placeholder above with:
            <iframe src="https://cal.com/your-link" className="w-full border-0 rounded-xl" style={{ minHeight: 600 }} />
          */}
        </div>

        {/* Prep Checklist */}
        <div className="bg-white rounded-2xl p-6 md:p-8 mb-10" style={{ border: "1px solid #E5E7EB" }}>
          <h3 className="font-bold text-base mb-4" style={{ color: "#1F2937" }}>📋 Prepare for Your Call</h3>
          <div className="space-y-3">
            {prepChecklist.map((item, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ border: "1.5px solid #E5E7EB" }}>
                  <span className="text-xs" style={{ color: "#9CA3AF" }}>{i + 1}</span>
                </div>
                <span className="text-sm" style={{ color: "#374151" }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* What to Expect */}
        <div className="rounded-2xl p-6 md:p-8 mb-10" style={{ backgroundColor: "#F8F7FF" }}>
          <h3 className="font-bold text-base mb-4" style={{ color: "#1F2937" }}>What Happens on the Call</h3>
          <div className="space-y-3">
            {[
              "We review your content, audience, and goals",
              "I map out your personalised monetisation roadmap",
              "We identify your best first (or next) digital product",
              "If it's a fit, I'll explain exactly how we'd work together",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#7C3AED" }} />
                <span className="text-sm" style={{ color: "#374151" }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <footer className="py-8" style={{ borderTop: "1px solid #E5E7EB" }}>
          <div className="flex items-center justify-center gap-4 text-xs" style={{ color: "#9CA3AF" }}>
            <Link to="/privacy-policy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-gray-600 transition-colors">Terms</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
